/**
 * The station, as a kitchen screen holds it.
 *
 * Specification: ../test/station.spec.ts. Master plan §4 M5, §3 K10.
 *
 * `@gf/module-kitchen` has had the whole board since it was written — states,
 * ages, the allergen gate, the debounce constant — and no screen. This is the
 * layer between: it holds the board, turns a bump into an event, and refuses
 * what the fold would only be able to record as having happened.
 *
 * IT IS OPTIMISTIC, AND THAT IS THE DESIGN. A cook bumps and the ticket leaves
 * the screen immediately, before anything reaches the server. A kitchen at
 * service does not wait for a round trip, and a screen that hesitates for
 * 200 ms gets hit again — which is precisely what the debounce exists to
 * survive. The event goes to the outbox and drains when it can.
 *
 * EVERY COMMAND IS ASYNC AND AWAITS THE OUTBOX. They were synchronous, firing
 * the enqueue and returning — so a caller could not know when a bump was
 * durable, an enqueue failure vanished into a floating promise, and a probe
 * that drained immediately after a tap found the queue empty. The screen still
 * updates before the await, which is what keeps it instant.
 *
 * THE OTHER SUBTLE PART IS THE REFRESH. The screen polls, so between an optimistic
 * bump and the next poll the server has not seen it: a naive install would put
 * the ticket back, the cook would watch a cleared ticket reappear, hit it again,
 * and the kitchen would stop believing the screen.
 */

import { BUMP_DEBOUNCE_MS } from "@gf/module-kitchen";
import type { AllergenCode, TicketModifier } from "@gf/contracts/values";
import type { OutboundEvent } from "@gf/sync-client";

export interface BoardItem {
  readonly selectionId: string;
  readonly name: string;
  readonly quantityMilli: number;
  readonly allergens: readonly AllergenCode[];
  /** Optional: a server older than modifiers sends neither. */
  readonly modifiers?: readonly TicketModifier[];
  readonly note?: string | null;
  readonly state: "PENDING" | "IN_PROGRESS" | "DONE";
  /** Taken off the check after it was fired. Not cooked. */
  readonly voided?: boolean;
}

export interface BoardTicket {
  readonly ticketId: string;
  readonly orderId: string;
  readonly stationId: string;
  readonly tableRef: string;
  readonly state: string;
  readonly age: "ON_TIME" | "CAUTION" | "LATE";
  readonly firedAt: string;
  readonly blockedReason: string | null;
  readonly recallCount: number;
  readonly acknowledged: readonly string[];
  readonly items: readonly BoardItem[];
  /** What the kitchen calls the table ("4", "Zahrádka 2"); null for a bar tab. */
  readonly tableName?: string | null;
  /** A counter sale's number ("A07"): what the cook calls out. */
  readonly pickupCode?: string | null;
  /** The course, when the table eats in courses. */
  readonly courseId?: string | null;
  /** Who fired it. */
  readonly server?: string | null;
}

/**
 * HOW MANY OF EACH DISH ARE WAITING, across the whole board — the number a
 * cook batches by ("four goulash, three schnitzel"). A dish voided or already
 * done is not waiting. Most first.
 */
export function allDay(tickets: readonly BoardTicket[]): { name: string; quantityMilli: number }[] {
  const counts = new Map<string, number>();
  for (const ticket of tickets) {
    for (const item of ticket.items) {
      if (item.voided || item.state === "DONE") continue;
      counts.set(item.name, (counts.get(item.name) ?? 0) + item.quantityMilli);
    }
  }
  return [...counts.entries()]
    .map(([name, quantityMilli]) => ({ name, quantityMilli }))
    .sort((a, b) => b.quantityMilli - a.quantityMilli || a.name.localeCompare(b.name));
}

/** How long after a bump the cook can take it back. */
export const RECALL_WINDOW_MS = 20_000;

/** PENDING, then IN_PROGRESS, then DONE. A cook does not un-cook a dish. */
const RANK: Readonly<Record<BoardItem["state"], number>> = {
  PENDING: 0,
  IN_PROGRESS: 1,
  DONE: 2,
};

export interface StationDeps {
  readonly outbox: { enqueue(event: OutboundEvent): Promise<void> };
  readonly stationId: string;
  /** Who is at this screen. Every event it emits carries it. */
  readonly cookId: string;
  readonly now: () => Date;
  readonly newId: () => string;
  readonly nextHlc: () => string;
}

export interface StationResult {
  readonly ok: boolean;
  /** Translatable. A kitchen screen never shows an English sentence. */
  readonly messageKey?: string;
}

const ok: StationResult = { ok: true };
const no = (messageKey: string): StationResult => ({ ok: false, messageKey });

export class StationSession {
  readonly #deps: StationDeps;

  /** The server's last word, plus whatever has happened here since. */
  #board: BoardTicket[] = [];

  /**
   * When each ticket was last bumped HERE. Never cleared.
   *
   * This is the debounce and nothing else, and it is separate from the hiding
   * below for a reason the tests found: if the two shared a map, a recall
   * arriving 100 ms after a bump would clear the entry, and a switch bounce
   * 200 ms after that would clear the REMAKE off the board instantly. The
   * bounce is a property of the hardware, not of what the server did next.
   */
  readonly #lastBumpAt = new Map<string, number>();

  /**
   * Tickets bumped here that the server has not confirmed yet.
   *
   * Purely optimistic display. Cleared by the server's own answer — a ticket
   * that genuinely went stops arriving, and a recall arrives with a higher
   * recall count, which outranks the local bump because the one screen that
   * cleared it must see the remake.
   */
  readonly #hidden = new Map<string, number>();

  /**
   * WHAT THIS SCREEN HAS DONE THAT THE SERVER HAS NOT SEEN YET.
   *
   * `install` replaces the board with the server's copy, and it only ever kept
   * local BUMPS. Everything else a cook does — acknowledging an allergen,
   * starting a dish, finishing one — was thrown away by the next poll, three
   * seconds later.
   *
   * On the pass that reads as a screen that forgets: the tick beside the
   * allergen disappears, the bump bar re-locks to "Nejdřív potvrďte alergeny",
   * and the cook taps again. During an OUTAGE — which this board is built to
   * survive, and the one time it matters most — the server never catches up at
   * all, so the ticket can never be cleared and the pass fills up.
   *
   * Kept until the server's own copy carries the same fact, exactly like
   * `#hidden` above, and for the same reason: the local view is AHEAD of the
   * server, not in conflict with it.
   */
  readonly #ackedLocally = new Map<string, Set<string>>();
  readonly #statesLocally = new Map<string, Map<string, BoardItem["state"]>>();

  /** The last ticket bumped here, for the moment in which it can be taken back. */
  #lastBumped: { ticket: BoardTicket; at: number } | null = null;
  /** Recalled here and not yet seen recalled by the server: kept on the board. */
  readonly #recalledLocally = new Map<string, BoardTicket>();

  constructor(deps: StationDeps) {
    this.#deps = deps;
  }

  /**
   * Take the server's board, keeping what this screen already knows.
   *
   * A RECALL OUTRANKS A LOCAL BUMP. A guest sent the food back; the one screen
   * that cleared it must see it again, or that station never cooks the remake.
   */
  install(tickets: readonly BoardTicket[]): void {
    const served = new Map(tickets.map((t) => [t.ticketId, t]));

    // A ticket recalled here stays on the board until the server says so too.
    for (const [ticketId, local] of this.#recalledLocally) {
      const incoming = served.get(ticketId);
      if (incoming && incoming.recallCount >= local.recallCount) this.#recalledLocally.delete(ticketId);
      else served.set(ticketId, local);
    }

    for (const [ticketId, recallCount] of this.#hidden) {
      const incoming = served.get(ticketId);
      // Gone from the server's board: the bump landed. Stop hiding it.
      if (!incoming) {
        this.#hidden.delete(ticketId);
        continue;
      }
      if (incoming.recallCount > recallCount) {
        this.#hidden.delete(ticketId);
        continue;
      }
      // Still there, not recalled: the server has not caught up. Hide it.
      served.delete(ticketId);
    }

    /**
     * Fold this screen's own work back over the server's copy.
     *
     * ITEM STATES ARE MONOTONIC — PENDING, then IN_PROGRESS, then DONE — so
     * "ahead" is well defined and the merge is a maximum rather than a guess.
     * A server that has caught up wins by being equal, and the local note is
     * dropped; a server that has gone FURTHER than this screen (another screen
     * on the same station finished the dish) wins outright.
     */
    for (const [ticketId, ticket] of served) {
      const acked = this.#ackedLocally.get(ticketId);
      const states = this.#statesLocally.get(ticketId);
      if (!acked && !states) continue;

      let merged = ticket;

      if (acked) {
        const already = new Set(ticket.acknowledged);
        const outstanding = [...acked].filter((id) => !already.has(id));
        if (outstanding.length === 0) this.#ackedLocally.delete(ticketId);
        else merged = { ...merged, acknowledged: [...ticket.acknowledged, ...outstanding] };
      }

      if (states) {
        let anyAhead = false;
        const items = merged.items.map((item) => {
          const local = states.get(item.selectionId);
          if (local === undefined || RANK[local] <= RANK[item.state]) return item;
          anyAhead = true;
          return { ...item, state: local };
        });
        if (anyAhead) merged = { ...merged, items };
        else this.#statesLocally.delete(ticketId);
      }

      served.set(ticketId, merged);
    }

    /**
     * A ticket the server no longer shows is finished with, whatever this
     * screen was holding for it. Left behind, these maps would grow for every
     * ticket a busy pass ever cleared.
     */
    for (const held of [this.#ackedLocally, this.#statesLocally]) {
      for (const ticketId of [...held.keys()]) {
        if (!served.has(ticketId)) held.delete(ticketId);
      }
    }

    this.#board = [...served.values()].sort((a, b) => a.firedAt.localeCompare(b.firedAt));
  }

  /** The ticket that can still be taken back, if the window is open. */
  recallable(): BoardTicket | null {
    const last = this.#lastBumped;
    if (!last || this.#deps.now().getTime() - last.at > RECALL_WINDOW_MS) return null;
    return last.ticket;
  }

  /**
   * Take back a bump made by mistake. Only the last one, only for a moment —
   * a recall later than that is the kitchen remaking food, which is a different
   * conversation and a different button.
   */
  async recall(ticketId: string): Promise<StationResult> {
    const last = this.recallable();
    if (!last || last.ticketId !== ticketId) return no("kds.error.unknownTicket");
    const back = { ...last, recallCount: last.recallCount + 1 };
    this.#lastBumped = null;
    this.#hidden.delete(ticketId);
    this.#recalledLocally.set(ticketId, back);
    this.#board = [...this.#board.filter((t) => t.ticketId !== ticketId), back].sort((a, b) =>
      a.firedAt.localeCompare(b.firedAt),
    );
    await this.#emit(last.orderId, "ticket.recalled", { ticketId, reason: "BUMPED_BY_MISTAKE" });
    return ok;
  }

  tickets(): readonly BoardTicket[] {
    return this.#board;
  }

  // --- The gate ------------------------------------------------------------

  /**
   * Which lines still need somebody to take responsibility.
   *
   * ONLY THE LINES THAT CARRY AN ALLERGEN. Asking a cook to tick a box on the
   * chips teaches them to tick every box, which is how the gate stops meaning
   * anything.
   */
  unacknowledged(ticketId: string): readonly BoardItem[] {
    const ticket = this.#board.find((t) => t.ticketId === ticketId);
    if (!ticket) return [];
    const done = new Set(ticket.acknowledged);
    // A voided dish is not being cooked: its allergens gate nothing.
    return ticket.items.filter((i) => !i.voided && i.allergens.length > 0 && !done.has(i.selectionId));
  }

  async acknowledge(ticketId: string, selectionId: string): Promise<StationResult> {
    const ticket = this.#board.find((t) => t.ticketId === ticketId);
    if (!ticket) return no("kds.error.unknownTicket");
    // A stale tap after a refresh. Emitting it would put an acknowledgement in
    // the log for a line nobody can point at.
    if (!ticket.items.some((i) => i.selectionId === selectionId)) {
      return no("kds.error.unknownItem");
    }

    this.#replace(ticketId, (t) => ({ ...t, acknowledged: [...t.acknowledged, selectionId] }));
    // Remembered, so the next poll does not take it back off the screen.
    const acked = this.#ackedLocally.get(ticketId) ?? new Set<string>();
    acked.add(selectionId);
    this.#ackedLocally.set(ticketId, acked);
    await this.#emit(ticket.orderId, "allergens.acknowledged", {
      ticketId,
      selectionId,
      // ATTRIBUTED. An acknowledgement made by nobody looks like one and is
      // worth less than none.
      acknowledgedBy: this.#deps.cookId,
    });
    return ok;
  }

  // --- Working a ticket ----------------------------------------------------

  start(ticketId: string, selectionId: string): Promise<StationResult> {
    return this.#move(ticketId, selectionId, "IN_PROGRESS", "item.started");
  }

  finish(ticketId: string, selectionId: string): Promise<StationResult> {
    return this.#move(ticketId, selectionId, "DONE", "item.finished");
  }

  async #move(
    ticketId: string,
    selectionId: string,
    state: BoardItem["state"],
    type: string,
  ): Promise<StationResult> {
    const ticket = this.#board.find((t) => t.ticketId === ticketId);
    if (!ticket) return no("kds.error.unknownTicket");
    if (!ticket.items.some((i) => i.selectionId === selectionId)) {
      return no("kds.error.unknownItem");
    }

    // Moved on screen FIRST. A cook who taps and sees nothing change taps
    // again, and the second tap starts somebody else's dish.
    this.#replace(ticketId, (t) => ({
      ...t,
      items: t.items.map((i) => (i.selectionId === selectionId ? { ...i, state } : i)),
    }));
    // Remembered, so the next poll does not put the dish back to PENDING.
    const states = this.#statesLocally.get(ticketId) ?? new Map<string, BoardItem["state"]>();
    states.set(selectionId, state);
    this.#statesLocally.set(ticketId, states);
    await this.#emit(ticket.orderId, type, { ticketId, selectionId });
    return ok;
  }

  // --- The bump bar --------------------------------------------------------

  /**
   * Clear the ticket.
   *
   * THE DEBOUNCE IS PER TICKET, not per screen. Two tickets ready at once and
   * the cook clears both; debouncing the whole screen would drop the second,
   * and the second is somebody's dinner.
   *
   * A swallowed bounce returns ok. It is not a failure — the cook pressed once
   * and the ticket cleared, which is exactly what they asked for.
   */
  async bump(ticketId: string): Promise<StationResult> {
    const at = this.#deps.now().getTime();

    const last = this.#lastBumpAt.get(ticketId);
    if (last !== undefined && at - last < BUMP_DEBOUNCE_MS) return ok;

    const ticket = this.#board.find((t) => t.ticketId === ticketId);
    if (!ticket) return no("kds.error.unknownTicket");

    // THE SAFETY RULE. A ticket carrying an unacknowledged allergen does not
    // leave the board, however hard the bar is hit.
    if (this.unacknowledged(ticketId).length > 0) {
      return no("kds.bump.allergensUnacknowledged");
    }

    /**
     * EVERY STATE CHANGE BEFORE THE FIRST await.
     *
     * The debounce entry, the hide and the board all move synchronously, so two
     * presses in the same tick cannot both get past the window check. Awaiting
     * first would let a bounce through the exact gap the debounce exists to
     * close.
     */
    this.#lastBumpAt.set(ticketId, at);
    this.#lastBumped = { ticket, at };
    this.#recalledLocally.delete(ticketId);
    this.#hidden.set(ticketId, ticket.recallCount);
    this.#board = this.#board.filter((t) => t.ticketId !== ticketId);

    await this.#emit(ticket.orderId, "ticket.bumped", { ticketId });
    return ok;
  }

  // --- Internals -----------------------------------------------------------

  #replace(ticketId: string, change: (ticket: BoardTicket) => BoardTicket): void {
    this.#board = this.#board.map((t) => (t.ticketId === ticketId ? change(t) : t));
  }

  async #emit(orderId: string, type: string, payload: Record<string, unknown>): Promise<void> {
    await this.#deps.outbox.enqueue({
      eventId: this.#deps.newId(),
      // THE ORDER IS THE STREAM, not the ticket. One order fired to the grill
      // and the bar is two tickets and one order, and every event of both has
      // to fold together with the selections they came from.
      streamId: orderId,
      type,
      payload,
      hlc: this.#deps.nextHlc(),
      occurredAt: this.#deps.now(),
      // The station is the actor. A kitchen display is shared and nobody signs
      // in to a wall screen mid-rush, so an acknowledgement in the log points
      // at a place — which is far better than pointing at nobody.
      actorId: this.#deps.cookId,
    });
  }
}
